import React, { useState, useEffect } from 'react';
import { PaymentRequest, PaymentSubmitRequest, Currency, Beneficiary, InvoiceDetails } from '../../types/customer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserSidebar from '../../components/sidebar/UserSidebar';
import './PaymentSubmissionPage.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import {
  submitPaymentRequest,
  resetSubmissionSuccess,
  clearError,
} from '../../redux/paymentSlice';
import { ratesApi, paymentsApi, beneficiaryApi } from '../../services/api';
import ValidationErrorDisplay from '../../components/ValidationErrorDisplay';
import PaymentValidation from '../../components/PaymentValidation/PaymentValidation';
import { validateWalletBalance } from '../../redux/slices/walletSlice';
// Optional: uncomment if using toast notifications
// import { toast } from 'react-toastify';

const PROCESSING_FEE_USD = 30; // $30 flat fee for USD payments

const getCompanyPaymentEmails = () => {
  const configuredEmails =
    process.env.REACT_APP_COMPANY_PAYMENT_EMAILS ||
    process.env.REACT_APP_COMPANY_PAYMENT_EMAIL ||
    'payments@kenluk.com';

  return configuredEmails
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
};

const defaultInvoiceDetails: InvoiceDetails = {
  originalPayerName: '',
  originalPayerAddress: {
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
  },
};

const PaymentSubmissionPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, submissionSuccess } = useSelector(
    (state: RootState) => state.payment
  );
  const { balanceValidation, loading: walletLoading } = useSelector(
    (state: RootState) => state.wallet
  );
  const user = useSelector((state: RootState) => state.auth.user);
  const isCompanyPaymentAccount =
    user?.accountType === 'company' ||
    getCompanyPaymentEmails().includes(user?.email?.toLowerCase() || '');

  const [payment, setPayment] = useState<Omit<PaymentRequest, 'status' | 'invoiceUrl' | '_id' | 'createdAt' | 'invoiceFileName' | 'invoiceOriginalFileName' | 'invoiceS3Key' | 'invoiceS3Bucket' | 'invoiceFileSize' | 'invoiceMimeType'>>({
    recipientCompany: '',
    recipientBank: '',
    recipientBankSwiftCode: '',
    accountNumber: '',
    recipientBankCountry: '',
    recipientAddress: '',
    recipientBankAddress: '',
    foreignAmount: 0,
    foreignCurrency: 'USD',
    localAmount: 0,
    exchangeRate: 0, // Will be fetched live
  });
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails>(defaultInvoiceDetails);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string>('');
  const [liveRate, setLiveRate] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string }>>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currencies: Currency[] = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  ];

  const fetchAdminRate = async () => {
    try {
      const data = await ratesApi.getUsdNgnRate();
      if (data && data.usdToNgnRate) {
        const rate = data.usdToNgnRate;
        setLiveRate(rate);
        setPayment((prev) => ({
          ...prev,
          exchangeRate: rate,
        }));
        console.log('✅ Using admin-configured rate:', rate);
      } else {
        throw new Error('Invalid rate data');
      }
    } catch (error) {
      console.error('Failed to fetch admin rate:', error);
      setLiveRate(0);
    }
  };

  useEffect(() => {
    fetchAdminRate();
    // Rates should rarely change, but check every 5 minutes for updates
    const interval = setInterval(fetchAdminRate, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadBeneficiaries = async () => {
      try {
        const response = await beneficiaryApi.getMyBeneficiaries();
        setBeneficiaries(response.beneficiaries || []);
      } catch (err) {
        console.error('Failed to load beneficiaries:', err);
        setBeneficiaries([]);
      }
    };
    loadBeneficiaries();
  }, []);

  useEffect(() => {
    if (submissionSuccess) {
      // toast.success('Payment request submitted successfully!');
      alert('Payment request submitted successfully!');
      dispatch(resetSubmissionSuccess());
      setPayment({
        recipientCompany: '',
        recipientBank: '',
        recipientBankSwiftCode: '',
        accountNumber: '',
        recipientBankCountry: '',
        recipientAddress: '',
        recipientBankAddress: '',
        foreignAmount: 0,
        foreignCurrency: 'USD',
        localAmount: 0,
        exchangeRate: liveRate || 1500,
      });
      setInvoiceFile(null);
      setInvoiceDetails(defaultInvoiceDetails);
      beneficiaryApi.getMyBeneficiaries()
        .then((response) => setBeneficiaries(response.beneficiaries || []))
        .catch(() => undefined);
      setIsSubmitting(false);
    }
  }, [submissionSuccess, dispatch, liveRate]);

  useEffect(() => {
    if (error) {
      // Check if error is from backend validation
      const errorData = typeof error === 'string' ? { message: error } : (error as any);
      
      // If error contains validation errors from backend
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        setValidationErrors(errorData.errors);
        setShowErrorModal(true);
      } else if (errorData?.errors && typeof errorData.errors === 'object') {
        // Convert error object to array
        const errorArray = Object.entries(errorData.errors).map(([field, messages]: any) => ({
          field,
          message: Array.isArray(messages) ? messages[0] : messages
        }));
        setValidationErrors(errorArray);
        setShowErrorModal(true);
      } else {
        // Generic error - show as alert
        alert(`Error: ${errorData?.message || error}`);
      }
      dispatch(clearError());
      setIsSubmitting(false);
    }
  }, [error, dispatch]);

  const handleInputChange = (field: keyof Omit<PaymentRequest, 'status' | 'invoiceUrl' | '_id' | 'createdAt'>, value: string | number | File | null) => {
    setPayment((prev) => {
      let updatedPayment = { ...prev, [field]: value };
      if (
        field === 'foreignAmount' ||
        field === 'foreignCurrency' ||
        field === 'exchangeRate'
      ) {
        const foreignAmount =
          field === 'foreignAmount' ? Number(value) : prev.foreignAmount;
        const exchangeRate =
          field === 'exchangeRate' ? Number(value) : prev.exchangeRate;
        updatedPayment.localAmount = foreignAmount * exchangeRate;
      }
      return updatedPayment;
    });
  };

  const handleFileChange = (file: File | null) => {
    setInvoiceFile(file);
  };

  const handleInvoiceDetailsChange = (
    field: keyof InvoiceDetails['originalPayerAddress'] | 'originalPayerName',
    value: string
  ) => {
    setInvoiceDetails((prev) => {
      if (field === 'originalPayerName') {
        return { ...prev, originalPayerName: value };
      }

      return {
        ...prev,
        originalPayerAddress: {
          ...prev.originalPayerAddress,
          [field]: value,
        },
      };
    });
  };

  const handleSelectBeneficiary = (beneficiaryId: string) => {
    setSelectedBeneficiaryId(beneficiaryId);
    const selected = beneficiaries.find((b) => b._id === beneficiaryId);
    if (!selected) return;

    setPayment((prev) => ({
      ...prev,
      recipientCompany: selected.recipientCompany,
      recipientBank: selected.recipientBank,
      recipientBankSwiftCode: selected.recipientBankSwiftCode,
      accountNumber: selected.accountNumber,
      recipientBankCountry: selected.recipientBankCountry,
      recipientAddress: selected.recipientAddress,
      recipientBankAddress: selected.recipientBankAddress
    }));
  };

  // Validation helper functions to reduce complexity
  const validateRecipientField = (field: string, value: string, minLength: number = 2, maxLength: number = 100, fieldLabel: string = ''): Array<{ field: string; message: string }> => {
    const errors: Array<{ field: string; message: string }> = [];
    const trimmedValue = value.trim();
    
    if (!trimmedValue) {
      errors.push({ field, message: `${fieldLabel} is required` });
    } else if (trimmedValue.length < minLength) {
      errors.push({ field, message: `${fieldLabel} must be at least ${minLength} characters` });
    } else if (trimmedValue.length > maxLength) {
      errors.push({ field, message: `${fieldLabel} must not exceed ${maxLength} characters` });
    }
    
    return errors;
  };

  const validateSwiftCode = (code: string, countryName?: string): Array<{ field: string; message: string }> => {
    const errors: Array<{ field: string; message: string }> = [];
    const trimmedCode = code.trim();
    
    if (!trimmedCode) {
      errors.push({ field: 'recipientBankSwiftCode', message: 'SWIFT code is required' });
    } else if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(trimmedCode)) {
      errors.push({ field: 'recipientBankSwiftCode', message: 'Invalid SWIFT code format (should be 8-11 characters, e.g., ABCDEFGH or ABCDGH2AXXX)' });
    } else if (countryName) {
      const countryCodeMap: Record<string, string> = {
        'china': 'CN',
        'hong kong': 'HK',
        'hongkong': 'HK',
        'hong kong sar': 'HK',
        'nigeria': 'NG',
        'united states': 'US',
        'united states of america': 'US',
        'united kingdom': 'GB',
        'great britain': 'GB',
        'england': 'GB',
        'germany': 'DE',
        'france': 'FR',
        'japan': 'JP',
        'canada': 'CA',
        'australia': 'AU',
        'switzerland': 'CH',
        'puerto rico': 'PR',
        'guam': 'GU',
        'american samoa': 'AS',
        'u.s. virgin islands': 'VI',
        'us virgin islands': 'VI',
        'virgin islands': 'VI',
        'northern mariana islands': 'MP'
      };
      
      const US_TERRITORIES = ['AS', 'GU', 'PR', 'VI', 'MP'];
      
      const rawCountry = countryName.trim();
      const normalizedCountryKey = rawCountry.toLowerCase();
      const providerCountry =
        countryCodeMap[normalizedCountryKey] ||
        (rawCountry.length === 2 ? rawCountry.toUpperCase() : rawCountry);

      const swiftCountry = trimmedCode.substring(4, 6).toUpperCase();
      
      const isSwiftUs = swiftCountry === 'US' || US_TERRITORIES.includes(swiftCountry);
      const isProviderUs = providerCountry === 'US' || US_TERRITORIES.includes(providerCountry);
      
      const isMatch = (swiftCountry === providerCountry) || (isSwiftUs && isProviderUs);

      if (!isMatch) {
        errors.push({
          field: 'recipientBankSwiftCode',
          message: `SWIFT country code (${swiftCountry}) must match the recipient bank country (${providerCountry})`
        });
      }
    }
    
    return errors;
  };

  const validateInvoiceFile = (file: File | null): Array<{ field: string; message: string }> => {
    const errors: Array<{ field: string; message: string }> = [];
    
    if (file) {
      const validMimeTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validMimeTypes.includes(file.type)) {
        errors.push({ field: 'invoiceMimeType', message: `Invalid invoice file type. Supported types: PDF, JPG, PNG, JPEG` });
      }
      if (file.size > 10 * 1024 * 1024) {
        errors.push({ field: 'invoiceFileSize', message: 'Invoice file must not exceed 10MB' });
      }
    } else {
      errors.push({ field: 'invoiceFileName', message: 'Invoice document is required. Please upload a PDF, JPG, PNG, or JPEG file' });
    }
    
    return errors;
  };

  const validatePaymentAmount = (amount: number): Array<{ field: string; message: string }> => {
    const errors: Array<{ field: string; message: string }> = [];
    
    if (amount <= 0) {
      errors.push({ field: 'foreignAmount', message: 'Amount must be greater than 0' });
    }
    
    return errors;
  };

  const validateInvoiceDetails = (details: InvoiceDetails): Array<{ field: string; message: string }> => {
    return [
      ...validateRecipientField('originalPayerName', details.originalPayerName, 2, 100, 'Original payer name'),
      ...validateRecipientField('payerStreetAddress', details.originalPayerAddress.streetAddress, 2, 200, 'Payer street address'),
      ...validateRecipientField('payerCity', details.originalPayerAddress.city, 2, 80, 'Payer city'),
      ...validateRecipientField('payerState', details.originalPayerAddress.state, 2, 80, 'Payer state'),
      ...validateRecipientField('payerCountry', details.originalPayerAddress.country, 2, 2, 'Payer country code'),
      ...validateRecipientField('payerPostalCode', details.originalPayerAddress.postalCode, 2, 20, 'Payer postal code'),
    ];
  };

  const getSubmitButtonText = (isLoading: boolean, isValidating: boolean): string => {
    if (isLoading) return 'Submitting...';
    if (isValidating) return 'Validating Balance...';
    return 'Submit Payment Request';
  };

  const validateAllPaymentFields = (paymentData: typeof payment, invoiceFileData: File | null, invoiceDetailsData: InvoiceDetails): Array<{ field: string; message: string }> => {
    return [
      ...validateRecipientField('recipientCompany', paymentData.recipientCompany, 2, 100, 'Recipient company name'),
      ...validateRecipientField('recipientBank', paymentData.recipientBank, 2, 100, 'Recipient bank name'),
      ...validateSwiftCode(paymentData.recipientBankSwiftCode, paymentData.recipientBankCountry),
      ...validateRecipientField('accountNumber', paymentData.accountNumber, 8, 34, 'Account number'),
      ...validateRecipientField('recipientBankCountry', paymentData.recipientBankCountry, 2, 50, 'Country'),
      ...validateRecipientField('recipientAddress', paymentData.recipientAddress, 10, 200, 'Recipient address'),
      ...validateRecipientField('recipientBankAddress', paymentData.recipientBankAddress, 10, 200, 'Bank address'),
      ...validatePaymentAmount(paymentData.foreignAmount),
      ...validateInvoiceFile(invoiceFileData),
      ...validateInvoiceDetails(invoiceDetailsData),
    ];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setValidationErrors([]);
    setShowErrorModal(false);

    // First check wallet balance
    if (!isCompanyPaymentAccount && !balanceValidation?.isValid) {
      // Check balance against local amount
      await dispatch(validateWalletBalance(payment.localAmount));
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    // Validate required fields using helpers
    const errors = validateAllPaymentFields(payment, invoiceFile, invoiceDetails);

    // Show validation errors if any
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    // Validate invoice file exists
    if (!invoiceFile) {
      setValidationErrors([{ field: 'invoiceFile', message: 'Invoice file is required' }]);
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    let invoiceData: {
      invoiceS3Key: string;
      invoiceBucketName: string;
      invoiceFileName: string;
      invoiceFileSize: number;
      invoiceMimeType: string;
    };

    try {
      const { uploadUrl, s3Key, bucketName } = await paymentsApi.getUploadInvoiceUrl({
        fileName: invoiceFile.name,
        fileSize: invoiceFile.size,
        mimeType: invoiceFile.type,
      });
      await fetch(uploadUrl, {
        method: 'PUT',
        body: invoiceFile,
        headers: {
          'Content-Type': invoiceFile.type,
        },
      });
      invoiceData = {
        invoiceS3Key: s3Key,
        invoiceBucketName: bucketName,
        invoiceFileName: invoiceFile.name,
        invoiceFileSize: invoiceFile.size,
        invoiceMimeType: invoiceFile.type,
      };
    } catch (error) {
      setValidationErrors([{ field: 'invoiceFileName', message: 'Invoice upload failed. Please try again.' }]);
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    // Compose payload for submitPaymentRequest thunk
    const submitPayload: PaymentSubmitRequest = {
      recipientCompany: payment.recipientCompany.trim(),
      recipientBank: payment.recipientBank.trim(),
      recipientBankSwiftCode: payment.recipientBankSwiftCode.trim(),
      accountNumber: payment.accountNumber.trim(),
      recipientBankCountry: payment.recipientBankCountry.trim(),
      recipientAddress: payment.recipientAddress.trim(),
      recipientBankAddress: payment.recipientBankAddress.trim(),
      foreignAmount: payment.foreignAmount,
      foreignCurrency: payment.foreignCurrency,
      localAmount: payment.localAmount,
      exchangeRate: payment.exchangeRate,
      invoiceS3Key: invoiceData.invoiceS3Key,
      invoiceBucketName: invoiceData.invoiceBucketName,
      invoiceFileName: invoiceData.invoiceFileName,
      invoiceFileSize: invoiceData.invoiceFileSize,
      invoiceMimeType: invoiceData.invoiceMimeType,
      invoiceDetails: {
        originalPayerName: invoiceDetails.originalPayerName.trim(),
        originalPayerAddress: {
          streetAddress: invoiceDetails.originalPayerAddress.streetAddress.trim(),
          city: invoiceDetails.originalPayerAddress.city.trim(),
          state: invoiceDetails.originalPayerAddress.state.trim().toUpperCase(),
          country: invoiceDetails.originalPayerAddress.country.trim().toUpperCase(),
          postalCode: invoiceDetails.originalPayerAddress.postalCode.trim(),
        },
      },
    };

    dispatch(submitPaymentRequest(submitPayload));
  };

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getFileName = (file: File | null | undefined) => {
    return file ? file.name : 'No file chosen';
  };

  // Compute fee breakdown for display
  const feeBreakdown = payment.foreignCurrency === 'USD' && payment.foreignAmount > 0
    ? {
        charged: payment.foreignAmount,
        fee: PROCESSING_FEE_USD,
        beneficiaryReceives: Math.max(0, payment.foreignAmount - PROCESSING_FEE_USD),
      }
    : null;

  return (
    <DashboardLayout sidebar={<UserSidebar />}>
      {/* Validation Error Modal */}
      {showErrorModal && (
        <ValidationErrorDisplay
          errors={validationErrors}
          onDismiss={() => setShowErrorModal(false)}
        />
      )}
      
      <div className="payment-submission-page">
        <div className="page-header">
          <h2>Payment Submission</h2>
          <p>Submit international payment requests for approval</p>
        </div>

        {submissionSuccess ? (
          <div className="submission-success">
            <div className="success-icon">✅</div>
            <h3>Payment Request Submitted!</h3>
            <p>Your payment request has been submitted successfully.</p>
            <p className="status-message">
              Status: <span className="status-pending">Pending Admin Approval</span>
            </p>
            <p className="success-note">
              You will receive a notification once your payment is processed.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="payment-form" noValidate>
            <div className="form-section">
              <h3>Recipient Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="savedBeneficiaries">Saved Beneficiaries (Callbook)</label>
                  <select
                    id="savedBeneficiaries"
                    value={selectedBeneficiaryId}
                    onChange={(e) => handleSelectBeneficiary(e.target.value)}
                  >
                    <option value="">Select a beneficiary</option>
                    {beneficiaries.map((beneficiary) => (
                      <option key={beneficiary._id} value={beneficiary._id}>
                        {beneficiary.recipientCompany} - {beneficiary.recipientBank} ({beneficiary.accountNumber})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipientCompany">Recipient Company Name *</label>
                  <input
                    type="text"
                    id="recipientCompany"
                    value={payment.recipientCompany}
                    onChange={(e) => handleInputChange('recipientCompany', e.target.value)}
                    placeholder="Enter company name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recipientAddress">Recipient Address *</label>
                  <input
                    type="text"
                    id="recipientAddress"
                    value={payment.recipientAddress}
                    onChange={(e) => handleInputChange('recipientAddress', e.target.value)}
                    placeholder="Enter recipient address"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipientBank">Recipient Bank Name *</label>
                  <input
                    type="text"
                    id="recipientBank"
                    value={payment.recipientBank}
                    onChange={(e) => handleInputChange('recipientBank', e.target.value)}
                    placeholder="Enter bank name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recipientBankSwiftCode">Recipient Bank SWIFT Code *</label>
                  <input
                    type="text"
                    id="recipientBankSwiftCode"
                    value={payment.recipientBankSwiftCode}
                    onChange={(e) => handleInputChange('recipientBankSwiftCode', e.target.value)}
                    placeholder="Enter SWIFT code"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="accountNumber">Account Number *</label>
                  <input
                    type="text"
                    id="accountNumber"
                    value={payment.accountNumber}
                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    placeholder="Enter account number"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipientBankCountry">Recipient Bank Country *</label>
                  <input
                    type="text"
                    id="recipientBankCountry"
                    value={payment.recipientBankCountry}
                    onChange={(e) => handleInputChange('recipientBankCountry', e.target.value)}
                    placeholder="Enter bank country"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recipientBankAddress">Recipient Bank Address *</label>
                  <input
                    type="text"
                    id="recipientBankAddress"
                    value={payment.recipientBankAddress}
                    onChange={(e) => handleInputChange('recipientBankAddress', e.target.value)}
                    placeholder="Enter bank address"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Payment Details</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="foreignAmount">Amount in Foreign Currency *</label>
                  <input
                    type="number"
                    id="foreignAmount"
                    value={payment.foreignAmount || ''}
                    onChange={(e) => handleInputChange('foreignAmount', e.target.valueAsNumber || 0)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="foreignCurrency">Currency *</label>
                  <select
                    id="foreignCurrency"
                    value={payment.foreignCurrency}
                    onChange={(e) => handleInputChange('foreignCurrency', e.target.value)}
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="amount-calculation">
                <div className="calculation-row">
                  <span>Exchange Rate:</span>
                  <span>1 {payment.foreignCurrency} = {payment.exchangeRate.toFixed(2)} NGN</span>
                </div>
                <div className="calculation-row">
                  <span>Amount in NGN:</span>
                  <span className="local-amount">
                    {formatCurrency(payment.localAmount, 'NGN')}
                  </span>
                </div>
                <div className="calculation-note">
                  * Local currency amount = Foreign amount × exchange rate
                  <br />
                  * Rate set by platform admin (not a market rate)
                </div>
              </div>

              {/* $30 SWIFT Fee Breakdown – USD only */}
              {feeBreakdown && (
                <div className="fee-breakdown-card">
                  <div className="fee-breakdown-header">
                    <span className="fee-breakdown-icon">💳</span>
                    <span className="fee-breakdown-title">SWIFT Fee Breakdown</span>
                  </div>
                  <div className="fee-breakdown-body">
                    <div className="fee-breakdown-row">
                      <span className="fee-breakdown-label">Amount You Enter</span>
                      <span className="fee-breakdown-value">{formatCurrency(feeBreakdown.charged, 'USD')}</span>
                    </div>
                    <div className="fee-breakdown-row fee-deduction-row">
                      <span className="fee-breakdown-label">
                        <span className="fee-deduction-dot">−</span> SWIFT Fee
                      </span>
                      <span className="fee-breakdown-value fee-deduction-value">
                        − {formatCurrency(feeBreakdown.fee, 'USD')}
                      </span>
                    </div>
                    <div className="fee-breakdown-divider" />
                    <div className="fee-breakdown-row fee-total-row">
                      <span className="fee-breakdown-label fee-total-label">✅ Beneficiary Receives</span>
                      <span className="fee-breakdown-value fee-total-value">
                        {formatCurrency(feeBreakdown.beneficiaryReceives, 'USD')}
                      </span>
                    </div>
                  </div>
                  <p className="fee-breakdown-note">
                    A flat <strong>$30 USD</strong> SWIFT fee applies to all USD payments.
                    Your beneficiary will receive the amount shown above.
                  </p>
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Invoice Payer Details</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="originalPayerName">Original Payer Name *</label>
                  <input
                    type="text"
                    id="originalPayerName"
                    value={invoiceDetails.originalPayerName}
                    onChange={(e) => handleInvoiceDetailsChange('originalPayerName', e.target.value)}
                    placeholder="Your Company Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="payerStreetAddress">Street Address *</label>
                  <input
                    type="text"
                    id="payerStreetAddress"
                    value={invoiceDetails.originalPayerAddress.streetAddress}
                    onChange={(e) => handleInvoiceDetailsChange('streetAddress', e.target.value)}
                    placeholder="123 Main St"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="payerCity">City *</label>
                  <input
                    type="text"
                    id="payerCity"
                    value={invoiceDetails.originalPayerAddress.city}
                    onChange={(e) => handleInvoiceDetailsChange('city', e.target.value)}
                    placeholder="Hong Kong"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="payerState">State/Region *</label>
                  <input
                    type="text"
                    id="payerState"
                    value={invoiceDetails.originalPayerAddress.state}
                    onChange={(e) => handleInvoiceDetailsChange('state', e.target.value)}
                    placeholder="HK"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="payerCountry">Country Code *</label>
                  <input
                    type="text"
                    id="payerCountry"
                    value={invoiceDetails.originalPayerAddress.country}
                    onChange={(e) => handleInvoiceDetailsChange('country', e.target.value)}
                    placeholder="HK"
                    maxLength={2}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="payerPostalCode">Postal Code *</label>
                  <input
                    type="text"
                    id="payerPostalCode"
                    value={invoiceDetails.originalPayerAddress.postalCode}
                    onChange={(e) => handleInvoiceDetailsChange('postalCode', e.target.value)}
                    placeholder="000000"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Invoice Upload</h3>
              
              <div className="upload-group">
                <label htmlFor="invoiceFile">Invoice Document *</label>
                <div className="file-input-container">
                  <input
                    type="file"
                    id="invoiceFile"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                  />
                  <span className="file-name">{getFileName(invoiceFile)}</span>
                </div>
                <p className="file-help">Upload supporting invoice document (PDF, JPG, PNG)</p>
              </div>
            </div>

            {/* Balance Validation Section */}
            {!isCompanyPaymentAccount && payment.localAmount > 0 && !balanceValidation?.isValid && (
              <div className="form-section">
                <h3>Payment Validation</h3>
                <PaymentValidation amount={payment.localAmount} />
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                disabled={loading || isSubmitting || (!isCompanyPaymentAccount && walletLoading)}
                className="submit-btn"
              >
                {getSubmitButtonText(loading || isSubmitting, !isCompanyPaymentAccount && walletLoading)}
              </button>
            </div>

            <div className="form-footer">
              <p className="disclaimer">
                <strong>Note:</strong> All payment requests require administrative approval before processing. 
                You will be notified once your request is approved or if additional information is needed.
              </p>
              <p className="disclaimer">
                Beneficiaries from your submitted transactions are automatically saved in your callbook for reuse.
              </p>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PaymentSubmissionPage;
